import { truncateToDecimals } from './truncateToDecimals'
import { checkIfFiatCurrencyIsRTL } from './checkIfFiatCurrencyIsRTL'
import { extractFiatDecimalPlaces } from './extractFiatDecimalPlaces'
import { extractFiatSeparators } from './extractFiatSeparators'
import { extractFiatSymbol } from './extractFiatSymbol'

export interface FormatFiatCurrencyProps {
  value: number
  fiatCode?: string
  showDecimals?: boolean
  decimalPlaces?: number
}

export function formatFiatCurrency({
  value,
  fiatCode = 'USD',
  showDecimals = false,
  decimalPlaces,
}: FormatFiatCurrencyProps): string {
  const decimals = decimalPlaces ?? extractFiatDecimalPlaces(fiatCode)
  // Use currency-specific locale to ensure correct separators (like EUR using comma)
  const { decimal, thousand } = extractFiatSeparators({
    fiatCode,
    preferDeviceLocale: false, // Always use currency-specific locale for consistent formatting
  })
  const symbol = extractFiatSymbol({ fiatCode })
  const isRTL = checkIfFiatCurrencyIsRTL({ fiatCode })

  // Trunc value instead of rounding
  const truncatedValue = truncateToDecimals({ value, decimals })

  const fixedValue = truncatedValue.toFixed(decimals) // e.g. "1234567.89"

  // Split integer and decimal parts
  const [integerPart, decimalPart] = fixedValue.split('.')

  // If showDecimals is true or the decimal part is not zero, show decimals
  const shouldShowDecimals =
    showDecimals === true || (!showDecimals && Number(decimalPart || 0) > 0)

  // Add thousand separators using regex
  // e.g. "1234567" -> "1,234,567" or "1.234.567"
  const integerWithThousandSeparators = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    thousand,
  )

  // Join integer and decimal parts with correct decimal separator
  const formattedNumber = shouldShowDecimals
    ? `${integerWithThousandSeparators}${decimal}${decimalPart || '0'.repeat(decimals)}`
    : integerWithThousandSeparators

  // Combine symbol and formatted number
  return isRTL ? `${formattedNumber} ${symbol}` : `${symbol}${formattedNumber}`
}
